import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import resx from '../../../utilities/resx';
import { LOGGLY_LEVEL_ERROR, RESX_GLOBAL, RESX_REBLASTS, MIXPANEL_CONTENT } from '../../../Constants';
import CommentAttachment from '../Attachment/CommentAttachment';
import CommentAttachmentPreview from '../Attachment/CommentAttachmentPreview';
import SaveComment from '../../../API/SaveComment';
import { withMessage } from '../Message';
import LogglyLogger from '../../../API/Loggly';
import Mixpanel from '../../../API/Mixpanel';
import Avatar from '../Avatar';
import UserMention from '../UserMention';

const CommentAuthor = ({ data, setState, message, lazy, inputValue, setInputValue, textareaRef }) => {
    if (
        !_path(['RESAAS', 'User2', 'IsProfessional'], window) ||
        _path(['RESAAS', 'User2', 'IsPendingProfessional'], window)
    ) {
        return null;
    }

    const [buttonStatus, setButtonStatus] = useState('');
    const [messageStatus, setMessageStatus] = useState('');
    const [commentMessage, setCommentMessage] = useState('');
    const [showCommentActions, setShowCommentActions] = useState(false);
    const [textAreaHeight, setTextAreaHeight] = useState('');

    const handleInputClick = event => {
        handleInput(event);
        setShowCommentActions(true);
        textareaRef.current.focus();

        setState(prevState => ({
            ...prevState,
            commentsFetch: data.hasMoreCommentsToShow,
            commentsDisplayCount: Infinity,
            commentsRequestedByUser: true,
            commentsPendingId: prevState.commentsPendingId + 1,
            hasMoreCommentsToShow: false
        }));
    };

    useEffect(
        () => {
            if (data.commentsPendingId > 0) {
                // user clicked comment, apply focus and expand self
                setShowCommentActions(true);
            }
        },
        [data.commentsPendingId]
    );

    const websiteId = _path(['RESAAS', 'User2', 'WebsiteId'], window);
    const saveComment = () => {
        const commentData = {
            comment: {
                ContentId: data.ReblastId,
                ContentTypeId: 3,
                Text: inputValue
            }
        };

        if (data.commentsAttachmentsId && data.commentsAttachmentTypeId) {
            commentData.comment.AttachmentId = data.commentsAttachmentsId;
            commentData.comment.AttachmentTypeId = data.commentsAttachmentTypeId;
        }

        // check input has at least 1 character before submitting
        if (inputValue.length && inputValue.length < 1000 && buttonStatus === '') {
            // adding spinner
            setButtonStatus('submitting');

            SaveComment.add(commentData)
                .then(response => {
                    if (!response.data.SaveCommentResult.Success) {
                        setButtonStatus('');
                        throw new Error(response.data.SaveCommentResult.Message);
                    }

                    setInputValue('');
                    setTextAreaHeight('');
                    setCommentMessage('');

                    const responseData = response.data.SaveCommentResult.Data;
                    const newComment = {
                        ...responseData,
                        DomainUrl: window.RESAAS.User2.DomainUrl,
                        WebsiteId: window.RESAAS.User2.WebsiteId,
                        FirstName: window.RESAAS.User2.FirstName,
                        LastName: window.RESAAS.User2.LastName
                    };

                    setState(prevState => {
                        prevState.Comments.push(newComment);
                        return {
                            ...prevState,
                            commentsAttachmentsId: null,
                            commentsAttachmentTypeId: null,
                            commentsAttachmentsImage: null
                        };
                    });

                    Mixpanel.add(MIXPANEL_CONTENT, { content: 'Reblast Comment', from: window.pageType });
                })
                .catch(error => {
                    setButtonStatus('');
                    const errorMessage = resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE');
                    message.addMessage({ message: errorMessage, type: 'error' });

                    LogglyLogger.add(LOGGLY_LEVEL_ERROR, 'SaveCommentService.add from Reblast Details Page', error);
                })
                .finally(() => {
                    setButtonStatus('');
                });
        } else {
            setMessageStatus('failed');
            setCommentMessage(resx(RESX_GLOBAL, 'COMMENT_TEXT'));
        }
    };

    const handleInput = event => {
        const { value } = event.target;
        setInputValue(value);
        setTextAreaHeight(textareaRef.current.scrollHeight);
    };

    const handleBlur = () => {
        setInputValue(textareaRef.current.value);
        setTextAreaHeight(textareaRef.current.scrollHeight);
    };

    const handleCancel = () => {
        setShowCommentActions(false);
        setInputValue('');
        setTextAreaHeight('');
    };

    return (
        <React.Fragment>
            <div className="add-new-comment">
                <div className="re-new-comment-initial">
                    <div className="comment-user-photo-wrapper item-comment-image">
                        {websiteId && (
                            <Avatar
                                size="xxs"
                                websiteId={websiteId}
                                imageAttributes={{ title: resx(RESX_REBLASTS, 'SMALL_PROFILE_PICTURE_TITLE') }}
                                lazy={lazy}
                            />
                        )}
                    </div>

                    <div className="comment-add-content-wrapper">
                        <textarea
                            name="rePostCommentTextarea"
                            ref={textareaRef}
                            style={textAreaHeight ? { height: `${textAreaHeight}px` } : {}}
                            className="autogrow add-new-comment-textarea ui-autocomplete-input"
                            placeholder={resx(RESX_REBLASTS, 'COMMENT_AREA_PLACEHOLDER')}
                            rows="1"
                            id="rePostCommentTextarea"
                            autoComplete="off"
                            title=""
                            value={inputValue}
                            onChange={handleInput}
                            onBlur={handleBlur}
                            onClick={handleInputClick}
                        />
                        <UserMention inputValue={inputValue} setInputValue={setInputValue} inputRef={textareaRef}/>
                        {data.commentsAttachmentsImage && <CommentAttachmentPreview data={data} update={setState} />}
                    </div>
                </div>

                {showCommentActions && (
                    <React.Fragment>
                        <ul className="add-reblasts-actions list-inline add-comments-actions comment-buttons">
                            <CommentAttachment data={data} update={setState} />

                            <li className="actions-submit-reblasts pull-right">
                                <ul className="list-inline">
                                    <li className="cancel-comment-wrapper">
                                        <a href="#" className="cancel-comment" onClick={handleCancel}>
                                            {resx(RESX_REBLASTS, 'CANCEL_TEXT')}
                                        </a>
                                    </li>
                                    <li className="btn-post-comment-wrapper">
                                        <button
                                            type="button"
                                            className={`re-btn re-btn-small re-post-comment-btn ${buttonStatus}`}
                                            value="Comment"
                                            onClick={saveComment}
                                        >
                                            {resx(RESX_REBLASTS, 'COMMENT_TEXT')}
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>

                        <div className={`re-comments-message ${messageStatus}`}>{commentMessage}</div>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );
};

CommentAuthor.propTypes = {
    data: PropTypes.object.isRequired,
    lazy: PropTypes.bool,
    setState: PropTypes.func.isRequired,
    message: PropTypes.object
};

export default withMessage(CommentAuthor);
